import { useUsersMap } from '@components/ArtBoard/Threads'
import type { MonolithicBlock } from '@octopus-do/octopus-project-core'
import { createSelector } from '@reduxjs/toolkit'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { selectOpenTargets, selectUnread } from '@store/reducers/commentReads'
import { selectCommentsMap } from '@store/reducers/comments'
import { selectThreads } from '@store/reducers/threads'
import type { AppState } from '@store/types'
import { selectSelectedThread } from '@storeOld/reducers/canvas/canvas'
import { useSelector } from 'react-redux'

import type { Comment } from '@/types'
import type { ThreadListItem } from './types'
import {
  buildCommentableItems,
  buildThreadsList,
  type CommentablePlace,
} from './utils'

const byCreatedSorter = (a: ThreadListItem, b: ThreadListItem): number =>
  a.createdAt > b.createdAt ? -1 : 1

// PHP stores the kind of place a comment hangs on as a number
const COMMENTABLE_PAGE = 1
const COMMENTABLE_BLOCK = 2

const kindOf = (comments: Comment[]): CommentablePlace['kind'] | null => {
  const type = comments[0]?.commentableType

  if (type === COMMENTABLE_PAGE) {
    return 'page'
  }

  if (type === COMMENTABLE_BLOCK) {
    return 'block'
  }

  // a thread reply: it belongs to the pin's row, not to one of its own
  return null
}

const selectDependencies = (state: AppState) => state.relations.dependencies
const selectNodes = (state: AppState) => state.manager.project.nodes
const selectBlocks = (state: AppState) => state.manager.project.blocks

// Blocks and pages with comments, named and located in the tree. Places whose
// block or page is gone are dropped the same way pins on deleted pages are.
// Memoised: while the panel is open this runs on every store update, and on a
// big map a `nodes.find` per commented page would add up.
const selectCommentablePlaces = createSelector(
  [selectCommentsMap, selectDependencies, selectNodes, selectBlocks],
  (comments, dependencies, nodes, blocks): CommentablePlace[] => {
    const nodeTitles = new Map(nodes.map((node) => [node.id, node.title]))

    return Object.keys(comments).flatMap((id) => {
      const kind = kindOf(comments[id])
      const related = dependencies[id]

      if (kind === null || !related) {
        return []
      }

      const pageId = kind === 'page' ? id : (related.parents[0] ?? null)

      if (pageId === null) {
        return []
      }

      const title =
        kind === 'page'
          ? (nodeTitles.get(id) ?? '')
          : ((blocks[id] as MonolithicBlock | undefined)?.title ?? '')

      return [{ id, kind, pageId, title, comments: comments[id] }]
    })
  }
)

export const useThreadsList = (showResolved: boolean): ThreadListItem[] => {
  const threads = useSelector(selectThreads)
  const commentableIds = threads.map((t) => t.commentable)
  const actualIds = useSelector(
    migrationProjectSelector.getActualIds(commentableIds)
  )
  const users = useUsersMap()
  const selected = useSelector(selectSelectedThread)
  const comments = useSelector(selectCommentsMap)
  const { threads: unreadThreads, targets: unreadTargets } =
    useSelector(selectUnread)
  const open = useSelector(selectOpenTargets)
  const places = useSelector(selectCommentablePlaces)

  const actualIdsSet = new Set(actualIds)

  const actualThreads = threads
    .filter((t) => actualIdsSet.has(t.commentable))
    .filter((t) => showResolved || !t.resolved)

  return [
    ...buildThreadsList(
      selected,
      actualThreads,
      comments,
      users,
      unreadThreads
    ),
    ...buildCommentableItems(places, users, unreadTargets, open),
  ].sort(byCreatedSorter)
}
